import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SearchResult } from '../types';
import { getUserId } from '../firebase/user-service';
import { updateSubscription, getUserSearches, getUserProfile } from '../firebase/firestore';
import type { SubscriptionData } from '../firebase/firestore';

interface HistoryState {
  searches: SearchResult[];
  isLoadingSearches: boolean;
  isSubscribed: boolean;
  subscriptionPlan: 'pro' | 'max' | null;
  isLoadingSubscription: boolean;
  isSubscriptionLoaded: boolean; // Has subscription been loaded from Firestore this session?
  checkoutId?: string;
  productId?: string;
  userId: string;
  addSearch: (search: SearchResult) => void;
  updateSearch: (id: string, updates: Partial<SearchResult>) => void;
  updateProgress: (id: string, progress: number, status?: 'analyzing' | 'completed' | 'failed') => void;
  getSearch: (id: string) => SearchResult | undefined;
  setSubscribed: (subscribed: boolean, checkoutId?: string, productId?: string, customerEmail?: string) => void;
  loadSubscriptionFromFirestore: () => Promise<void>;
  loadSearchesFromFirestore: () => Promise<void>;
  clearHistory: () => void;
}

export const useHistoryStore = create<HistoryState>()(
  persist(
    (set, get) => ({
      searches: [],
      isLoadingSearches: false,
      isSubscribed: false,
      subscriptionPlan: null,
      isLoadingSubscription: false,
      isSubscriptionLoaded: false,
      checkoutId: undefined,
      productId: undefined,
      userId: getUserId(),

      addSearch: (search) => {
        // Only update local state - DO NOT write to Firestore
        // The search document is already created by the backend (initiateDeepSearch function)
        // Writing here would create a DUPLICATE document with a different ID
        set((state) => ({
          searches: [search, ...state.searches],
        }));
      },

      updateSearch: (id, updates) =>
        set((state) => ({
          searches: state.searches.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          ),
        })),

      updateProgress: (id, progress, status) =>
        set((state) => ({
          searches: state.searches.map((s) =>
            s.id === id
              ? {
                  ...s,
                  progress,
                  ...(status && { status }),
                }
              : s
          ),
        })),

      getSearch: (id) => get().searches.find((s) => s.id === id),

      setSubscribed: (subscribed, checkoutId, productId, customerEmail) => {
        const plan = productId?.includes(import.meta.env.VITE_POLAR_PRODUCT_PRO_ID) ? 'pro' : 'max';
        set({ 
          isSubscribed: subscribed, 
          subscriptionPlan: subscribed ? plan : null,
          checkoutId, 
          productId, 
          isSubscriptionLoaded: true 
        });

        // Sync subscription to Firestore
        const userId = get().userId;

        const subscriptionData: SubscriptionData = {
          isSubscribed: subscribed,
          plan,
          productId,
          checkoutId,
          customerEmail,
          status: 'active',
        };

        updateSubscription(userId, subscriptionData).catch((error) => {
          console.error('Failed to sync subscription to Firestore:', error);
        });
      },

      loadSubscriptionFromFirestore: async () => {
        const { isLoadingSubscription, isSubscriptionLoaded, userId } = get();

        // Already loaded or loading, skip
        if (isSubscriptionLoaded || isLoadingSubscription) {
          return;
        }

        set({ isLoadingSubscription: true });

        try {
          const userProfile = await getUserProfile(userId);

          if (userProfile?.subscription) {
            const sub = userProfile.subscription;
            set({
              isSubscribed: sub.isSubscribed,
              subscriptionPlan: sub.plan,
              productId: sub.productId || undefined,
              checkoutId: sub.checkoutId || undefined,
              isLoadingSubscription: false,
              isSubscriptionLoaded: true,
            });
          } else {
            // No subscription data found
            set({
              isSubscribed: false,
              subscriptionPlan: null,
              isLoadingSubscription: false,
              isSubscriptionLoaded: true,
            });
          }
        } catch (error) {
          console.error('Failed to load subscription from Firestore:', error);
          set({ isLoadingSubscription: false, isSubscriptionLoaded: true });
        }
      },

      loadSearchesFromFirestore: async () => {
        const userId = get().userId;
        set({ isLoadingSearches: true });
        try {
          const firestoreSearches = await getUserSearches(userId);
          // Always use Firestore as the source of truth
          set({ searches: firestoreSearches, isLoadingSearches: false });
        } catch (error) {
          console.error('Failed to load searches from Firestore:', error);
          set({ isLoadingSearches: false });
        }
      },

      clearHistory: () => set({ 
        searches: [], 
        isSubscribed: false, 
        subscriptionPlan: null,
        isLoadingSubscription: false,
        isSubscriptionLoaded: false,
        checkoutId: undefined, 
        productId: undefined 
      }),
    }),
    {
      name: 'deepcheck-history-storage',
      partialize: (state) => ({
        // Only persist userId - subscription status is loaded from Firestore
        userId: state.userId,
      }),
    }
  )
);
